import { BASE_URL } from '../config/api.config';

/**
 * Generic request helper used by every service function.
 * - Always hits `${BASE_URL}${path}` so the URL never needs to be repeated.
 * - Automatically attaches the auth token (if present) as a Bearer header.
 * - Normalizes the backend's { responseCode, responseMessage, data } envelope
 *   into a plain rejected/resolved Promise so callers can just try/catch.
 */
const request = async (path, { method = 'GET', body, headers = {} } = {}) => {
  const token = localStorage.getItem('authToken');

  let response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch (networkErr) {
    throw new Error('Unable to reach the server. Please check your connection and try again.');
  }

  let payload = null;
  try {
    payload = await response.json();
  } catch (parseErr) {
    payload = null;
  }

  const isSuccess =
    response.ok &&
    (!payload || payload.responseCode === undefined || payload.responseCode === '200' || payload.responseCode === 200);

  if (!isSuccess) {
    const message =
      (payload && (payload.responseMessage || payload.message)) ||
      `Request failed with status ${response.status}`;
    const error = new Error(message);
    error.status = response.status;
    error.payload = payload;
    throw error;
  }

  // Different backend responses wrap the useful payload under different keys
  // (e.g. { responseCode, responseMessage, users: {...} }). Unwrap it, but keep
  // any sibling fields (like a token returned alongside the user) intact too.
  let unwrapped = payload;
  if (payload && typeof payload === 'object') {
    const metaKeys = ['responseCode', 'responseMessage', 'message', 'status', 'success'];
    const envelopeKey = ['data', 'Data', 'users', 'Users', 'user', 'User', 'result', 'Result'].find((key) =>
      Object.prototype.hasOwnProperty.call(payload, key)
    );
    if (envelopeKey) {
      const inner = payload[envelopeKey];
      if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
        const siblings = {};
        Object.keys(payload).forEach((key) => {
          if (key !== envelopeKey && !metaKeys.includes(key)) siblings[key] = payload[key];
        });
        unwrapped = { ...siblings, ...inner };
      } else {
        unwrapped = inner;
      }
    }
  }

  // Keep the original response around (non-enumerable) so callers can inspect
  // it in the browser console when troubleshooting an unfamiliar response shape.
  if (unwrapped && typeof unwrapped === 'object') {
    Object.defineProperty(unwrapped, '__raw', { value: payload, enumerable: false });
  }

  return unwrapped;
};

export const apiGet = (path, opts) => request(path, { ...opts, method: 'GET' });
export const apiPost = (path, body, opts) => request(path, { ...opts, method: 'POST', body });
export const apiPut = (path, body, opts) => request(path, { ...opts, method: 'PUT', body });

export default request;
